import { Paper, Text, Group, Stack, Badge, useMantineTheme } from '@mantine/core';
import { Milestone, MilestoneStatus } from '../../types/milestones';
import { IconAlertOctagon, IconAlertTriangle, IconCalendarDue } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { AppTooltip } from '../common/AppTooltip';
import {
  formatMilestonePastDueLabel,
  getMilestoneDaysPastDue,
  getMilestonePastDueAccent,
  getMilestonePastDueBadgeStatus,
} from '../../utils/milestonePastDue';
import { formatTimeInMilestoneStatus } from '../../utils/milestoneStatusDuration';

// Status configuration
const statusConfig = {
  not_started: { label: 'Not Started', color: 'dark' },
  in_progress: { label: 'In Progress', color: 'blue' },
  blocked: { label: 'Blocked', color: 'red' },
  completed: { label: 'Completed', color: 'green' }
};

// Simple Status Badge Component
const SimpleStatusBadge = ({
  status,
  statusUpdatedAt,
}: {
  status: MilestoneStatus | string;
  statusUpdatedAt?: string | null;
}) => {
  const normalizedStatus =
    status && status in statusConfig ? (status as MilestoneStatus) : 'not_started';
  const config = statusConfig[normalizedStatus];
  const timeInStatusLabel =
    normalizedStatus === 'not_started'
      ? null
      : formatTimeInMilestoneStatus(statusUpdatedAt, config.label);

  const badge = (
    <Badge 
      color={config.color} 
      variant="filled" 
      size="sm"
      style={{ padding: '8px 12px' }}
    >
      {config.label}
    </Badge>
  );

  if (!timeInStatusLabel) {
    return badge;
  }

  return (
    <AppTooltip label={timeInStatusLabel} openDelay={100} withinPortal inline>
      {badge}
    </AppTooltip>
  );
};

interface MilestoneStatusCardProps {
  milestone: Milestone;
  activityTitle?: string;
  onToggle: (id: string, status: MilestoneStatus) => void;
}

export function MilestoneStatusCard({ 
  milestone, 
}: Readonly<MilestoneStatusCardProps>) {
  const theme = useMantineTheme();
  const pastDueAccent = getMilestonePastDueAccent(milestone.status, milestone.due_date);
  const pastDueStatus = getMilestonePastDueBadgeStatus(milestone.status, milestone.due_date);
  const daysPastDue = getMilestoneDaysPastDue(milestone.status, milestone.due_date);
  const pastDueLabel =
    daysPastDue != null ? formatMilestonePastDueLabel(daysPastDue) : 'Past Due';
  const pastDueColor =
    pastDueStatus === 'past_due_grace'
      ? theme.colors.yellow[6]
      : pastDueStatus === 'past_due'
        ? theme.colors.red[6]
        : null;
  const PastDueIcon = pastDueStatus === 'past_due_grace' ? IconAlertTriangle : IconAlertOctagon;

  // Extract numbers from activity_id and milestone id to create "1.1" format
  const getActivityNumber = (activityId: string | undefined): string => {
    if (!activityId) return '0';
    const match = activityId.match(/\d+/);
    return match ? parseInt(match[0], 10).toString() : '0';
  };
  
  const getMilestoneNumber = (milestoneId: string): string => {
    const match = milestoneId.match(/\d+/);
    return match ? parseInt(match[0], 10).toString() : '0';
  };
  
  const activityNumber = getActivityNumber(milestone.activity_id);
  const milestoneNumber = getMilestoneNumber(milestone.id);
  const milestoneLabel = `${activityNumber}.${milestoneNumber}`;
  
  return (
    <Paper 
      shadow="sm" 
      p="md" 
      radius="md" 
      withBorder
      aria-label={pastDueAccent ? `Milestone ${milestoneLabel}, Past Due` : undefined}
      sx={(theme) => ({
        height: '280px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
        ...(pastDueAccent
          ? { borderLeft: `3px solid ${theme.colors[pastDueAccent][6]}` }
          : {}),
        '&:hover': {
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1]
        }
      })}
    >
      <Stack spacing={4} style={{ flex: 1 }}>
        {/* Top section with milestone badge and status badge */}
        <Group position="apart" noWrap mb="md">
          <Badge size="sm" color="gray" variant="filled">{`Milestone ${milestoneLabel}`}</Badge>
          <SimpleStatusBadge
            status={milestone.status}
            statusUpdatedAt={
              milestone.status_updated_at ??
              (milestone as { statusUpdatedAt?: string }).statusUpdatedAt
            }
          />
        </Group>
        
        {/* Milestone title with adequate height for 4 lines */}
        <Text 
          weight={500} 
          size="md" 
          lineClamp={4}
          mb="md"
          sx={(theme) => ({
            textDecoration: milestone.completed ? 'line-through' : 'none',
            color: milestone.completed ? theme.colors.gray[6] : 'inherit',
            height: '100px', // Fixed height for title area
            overflow: 'hidden',
          })}
        >
          {milestone.title}
        </Text>
        
        {/* Due date + optional Past Due */}
        <Stack spacing={4} mb={12}>
          <Group spacing="xs" noWrap>
            <IconCalendarDue size={16} style={{ color: 'var(--mantine-color-dimmed)' }} aria-hidden />
            <Text 
              size="sm" 
              color="dimmed" 
              weight={500}
              sx={milestone.completed ? { textDecoration: 'line-through' } : {}}
            >
              {dayjs(milestone.due_date).format('MMM D, YYYY')}
            </Text>
          </Group>
          {pastDueStatus && pastDueColor ? (
            <Group spacing="xs" noWrap aria-label={pastDueLabel}>
              <PastDueIcon size={16} style={{ color: pastDueColor, display: 'block' }} aria-hidden />
              <Text size="sm" weight={600} style={{ color: pastDueColor }}>
                {pastDueLabel}
              </Text>
            </Group>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}
